import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LocationMap from '../components/LocationMap';

const Contact = () => {
  const [formType, setFormType] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    formData.append('formType', formType);

    fetch('/api/contact', {
      method: 'POST',
      body: formData,
    })
      .then((res) => {
        if (res.ok) {
          alert('Message sent!');
        } else {
          alert('There was an error submitting the form.');
        }
      })
      .catch(() => alert('Failed to send form.'));
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Redemption Psychiatry</title>
      </Helmet>

      <div className="wrap">
        <Header />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div id="content" role="main" className="home">
            {/* Interior Header */}
            <div id="interior-header-container" className="container-fluid">
              <div className="container">
                <h1>Contact Us</h1>
              </div>
            </div>

            {/* Contact Intro */}
            <div id="contact-introduction-container" className="container">
              <h2>We’re Here for You. Let’s Connect.</h2>
              <p>
                Seeking mental health care is a big step, and we’re here to make
                the process as easy, supportive, and stress-free as possible.
                Whether you have questions about our services, need to schedule
                an appointment, or require assistance with medication refills or
                billing, our team is ready to help.
              </p>
            </div>

            {/* Contact Types */}
            <div id="contact-types-container" className="container-fluid">
              <div className="container">
                <div>
                  <div>
                    <h3>Find a Location</h3>
                    <p>
                      In-person appointments at multiple locations and
                      telepsychiatry services for convenience and accessibility.
                    </p>
                    <a href="#locationsModal" data-bs-toggle="modal" className="button blue">
                      Find a Location
                    </a>
                  </div>
                  <div>
                    <h3>Patient Requests & Support</h3>
                    <p>
                      FAQs, forms, and important information on medication
                      refills.
                    </p>
                    <a href="/resources" className="button blue">
                      Patient Resources
                    </a>
                  </div>
                  <div>
                    <h3>Call Us</h3>
                    <p>
                      Call or text us to schedule a follow-up appointment or for any questions or concerns.
                    </p>
                    <a href="tel:+14804718560" className="button blue">
                      (480) 471-8560
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div id="contact-form-container" className="container large-container">
              <h2>Send Us a Message</h2>
              <p>
                Have a question? Fill out the form below, and we’ll get back to
                you within 1–2 business days. For immediate assistance, please
                call our office. If you are experiencing a crisis, call 911 or
                go to the nearest emergency room.
              </p>

              <div className="form-row mb-4">
                <label htmlFor="formType">
                  What are you contacting us about?<span>Required</span>
                </label>
                <select
                  id="formType"
                  className="form-control"
                  value={formType}
                  onChange={(e) => setFormType(e.target.value)}
                  required
                  defaultValue=""
                >
                  <option value="" disabled hidden>-- Select one --</option>
                  <option value="DBS">DBS Consultation</option>
                  <option value="KAT">Ketamine Assisted Therapy (KAT)</option>
                  <option value="INTAKE">New Patient Intake/Referral</option>
                  <option value="BILLING">Billing</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              {formType === 'INTAKE' ? (
                <div className="alert alert-info">
                  <p>
                    Redemption Psychiatry collaborates with advanced treatment teams. If you're seeking a new
                    patient intake, click the button below to complete the intake process with a provider.
                  </p>
                  <div className="text-center">
                    <a
                      href="https://www.rpsych.com/newpatients"
                      className="button blue"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Go to New Patient Form
                    </a>
                  </div>
                  <hr />
                  <p>
                    <strong>For Discharge Planners:</strong><br />
                    We understand the importance of coordinated care during the discharge process. Our team is
                    committed to working with discharge planners to ensure that patients transitioning from
                    inpatient to outpatient care receive the support they need. Please contact our Discharge
                    Coordinator to discuss how we can assist with your patient's discharge plan:
                  </p>
                  <p>
                    <a href="mailto:referrals@rpsych.com">referrals@rpsych.com</a><br />
                    <a href="tel:+14808704181">(480) 870-4181</a>
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}  style={{ marginTop: '0px' }}>
                  <div className="form-row split">
                    <div>
                      <label>
                        First Name <span>Required</span>
                      </label>
                      <input type="text" name="first_name" placeholder="First Name" required />
                    </div>
                    <div>
                      <label>
                        Last Name <span>Required</span>
                      </label>
                      <input type="text" name="last_name" placeholder="Last Name" required />
                    </div>
                  </div>

                  <div className="form-row split">
                    <div>
                      <label>
                        Email <span>Required</span>
                      </label>
                      <input type="email" name="email" placeholder="Email address" required />
                    </div>
                    <div>
                      <label>
                        Phone <span>Required</span>
                      </label>
                      <input type="tel" name="phone" placeholder="000-000-0000" required />
                    </div>
                  </div>

                  <div className="form-row">
                    <label>
                      Preferred Contact Method <span>Required</span>
                    </label>
                    <select name="preferred_contact_method" required>
                      <option value="" disabled hidden>-- Select One --</option>
                      <option value="Phone">Phone</option>
                      <option value="Email">Email</option>
                    </select>
                  </div>

                  <div className="form-row">
                    <label>
                      Date of Birth <span>Required</span>
                    </label>
                    <input type="date" name="dob" required />
                  </div>

                  <div className="form-row">
                    <label>
                      Message <span>Required</span>
                    </label>
                    <textarea name="message" placeholder="Write here..." required />
                  </div>

                  <input type="submit" value="Send" className="btn btn-primary mt-3" />
                </form>
              )}
            </div>

            {/* Emergency & Crisis Widget */}
            <div id="widgets-container" className="container">
              <div>
                <div className="blue">
                  <div>
                    <h3>Emergency & Crisis Support</h3>
                    <p>If you or someone you know is in crisis, help is available 24/7:</p>
                    <p>
                      <strong>
                        988 Suicide & Crisis Lifeline – Call or text 988<br />
                        <a href="https://988lifeline.org/" target="_blank" rel="noopener noreferrer" style={{ color: 'white', textDecoration: 'underline' }}>
                          Visit 988lifeline.org
                        </a><br />
                        Crisis Text Line – Text "HELLO" to 741741<br />
                        Emergency? Call 911 or visit the nearest ER.
                      </strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal fade"
            id="locationsModal"
            tabIndex="-1"
            aria-labelledby="locationsModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content" style={{ borderRadius: '20px', border: 'none' }}>
                <div
                  className="modal-header text-white"
                  style={{
                    background: '#3a66bd',
                    borderTopLeftRadius: '20px',
                    borderTopRightRadius: '20px',
                  }}
                >
                  <h5 className="modal-title text-center w-100" id="locationsModalLabel" style={{ fontWeight: 600 }}>
                    Our Locations
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body bg-light">
                  <div className="container">
                    <LocationMap />
                  </div>
                </div>

              </div>
            </div>
          </div>

        </motion.div>
        <Footer />
      </div>
    </>
  );
};

export default Contact;
